type PlainObject = Record<string, unknown>;

const _isObj = (x: unknown): x is PlainObject =>
    Object.prototype.toString.call(x) === "[object Object]";

const _isNumber = (x: unknown): x is number =>
    Object.prototype.toString.call(x) === "[object Number]";

export const FromObject = {
    /**
     * Convert an object into a 2D array ([[key, value], ...]).
     * @param inputObj Input object.
     */
    ObjectToArray(inputObj: Record<string | number, unknown>): [string, unknown][] {
        return Object.keys(inputObj).map((key) => [key, inputObj[key]]);
    },
    /**
     * Flatten a multi-level object into a single level.
     * Duplicate keys are given a generated ID when overwriteKey is false.
     * @param obj Object to flatten.
     * @param overwriteKey Overwrite duplicate keys (default false).
     */
    flatten(obj: object, overwriteKey: boolean = false): Record<string, unknown> {
        if (Object.keys(obj).length === 0) return {};
        const rs: Record<string, unknown> = {};
        const genId = (): string =>
            (Date.now() / 1000 | 0).toString(16) +
            "xxxxxxxxxxxxxxxx".replace(/x/g, () => (Math.random() * 16 | 0).toString(16));

        const recurse = (o: object): void => {
            for (const key in o) {
                const val = (o as Record<string, unknown>)[key];
                if (_isObj(val)) {
                    recurse(val);
                } else if (key in rs && !overwriteKey) {
                    rs[genId()] = val;
                } else {
                    rs[key] = val;
                }
            }
        };
        recurse(obj);
        return rs;
    },
    /**
     * Find and return the deepest nested object.
     * Returns the first deepest object found; or all if multi = true.
     * @param obj Input object.
     * @param multi Return all objects at the deepest level.
     */
    getDeepest(obj: object, multi: boolean = false): object | object[] {
        if (Object.keys(obj).length === 0) return {};
        let deepest = 0;
        let rs: object = {};
        const rsMulti: Record<number, object[]> = {};

        const recurse = (o: object, depth: number = 0): void => {
            depth++;
            if (depth > deepest) {
                deepest = depth;
                rs = o;
                if (multi) rsMulti[depth] = [o];
            } else if (depth === deepest && multi) {
                if (!rsMulti[depth]) rsMulti[depth] = [];
                rsMulti[depth].push(o);
            }
            for (const key in o) {
                const val = (o as Record<string, unknown>)[key];
                if (_isObj(val)) recurse(val, depth);
            }
        };
        recurse(obj);
        return multi ? (rsMulti[deepest] ?? []) : rs;
    },
    /**
     * Sum all numeric values in an object (supports single-level nesting via field).
     * @param obj Input object.
     * @param field Target field when values are nested objects.
     */
    sumAll(obj: object, field: string = ""): number {
        let sum = 0;
        for (const prop in obj) {
            const raw = (obj as Record<string, unknown>)[prop];
            const t = field !== "" ? (raw as Record<string, unknown>)[field] : raw;
            if (!_isNumber(t)) throw new Error(`Value of property "${prop}" is not a number.`);
            sum += t;
        }
        return sum;
    },
    /**
     * Find the maximum numeric value in an object.
     * @param obj Input object.
     * @param field Target field when values are nested objects.
     */
    max(obj: object, field: string = ""): number {
        let max = -Infinity;
        for (const prop in obj) {
            const raw = (obj as Record<string, unknown>)[prop];
            const t = field !== "" ? (raw as Record<string, unknown>)[field] : raw;
            if (!_isNumber(t)) throw new Error(`Value of property "${prop}" is not a number.`);
            if (t > max) max = t;
        }
        return max;
    },
    /**
     * Find the minimum numeric value in an object.
     * @param obj Input object.
     * @param field Target field when values are nested objects.
     */
    min(obj: object, field: string = ""): number | null {
        let min: number | null = null;
        for (const prop in obj) {
            const raw = (obj as Record<string, unknown>)[prop];
            const t = field !== "" ? (raw as Record<string, unknown>)[field] : raw;
            if (!_isNumber(t)) throw new Error(`Value of property "${prop}" is not a number.`);
            if (min === null || t < min) min = t;
        }
        return min;
    },
};
